import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import LandingFooter from "../components/LandingFooter";
import LandingPage from "./LandingPage";
import { API } from "../utils/constants";

const BROWN = "#51120A";

// Shared page shell: centered logo (no nav), homepage hero collage with the
// blue scalloped paper as the message stage, and the website footer.
const InviteHeroShell = ({ children, testId }) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: "#EEE7D8" }} data-testid={testId}>
      {/* Centered logo in place of the nav */}
      <div className="flex justify-center px-4 md:px-8 py-4 md:py-6">
        <img
          src="https://customer-assets.emergentagent.com/job_393ec875-54a1-44b5-aa19-8314f136e991/artifacts/5dh5037r_wellby-beta-logo.png"
          alt="Wellby Beta"
          style={{ height: "50px", width: "auto" }}
        />
      </div>

      {/* Hero area — mirrors the homepage hero layout */}
      <div className="relative">
        <div
          className="relative w-full h-full flex items-center justify-center"
          style={{ paddingBottom: isMobile ? "40px" : "24px", marginTop: isMobile ? "-10px" : "-40px" }}
        >
          {/* Decorative collage - tablet and desktop only */}
          <div className="hidden md:block absolute inset-0">
            {/* Eggplant sticky note - left */}
            <div
              className="absolute z-20"
              style={{
                left: isDesktop ? "calc(50% - 590px)" : "calc(50% - 350px)",
                top: isDesktop ? "calc(50% - 290px)" : "calc(50% - 230px)",
                width: isDesktop ? "270px" : "200px",
                height: isDesktop ? "270px" : "200px",
                backgroundImage: `url('/eggplant.png')`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                transform: isDesktop ? "rotate(-6deg)" : "rotate(4deg)",
              }}
            ></div>

            {/* Candles - left */}
            <div
              className="absolute"
              style={{
                left: isDesktop ? "calc(50% - 555px)" : "calc(50% - 365px)",
                top: isDesktop ? "calc(50% - 50px)" : "calc(50% + 40px)",
                width: isDesktop ? "250px" : "188px",
                height: isDesktop ? "250px" : "188px",
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_culinary-notes-5/artifacts/hi39abfs_candle.png')`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                zIndex: 25,
              }}
            ></div>

            {/* Polaroid frame - right */}
            {!isMobile && (
              <div
                className="absolute"
                style={{
                  left: isDesktop ? "calc(50% + 335px)" : "calc(50% + 193px)",
                  top: isDesktop ? "calc(50% - 280px)" : "calc(50% - 220px)",
                  width: isDesktop ? "200px" : "135px",
                  height: isDesktop ? "240px" : "162px",
                  backgroundImage: `url('https://customer-assets.emergentagent.com/job_culinary-notes-5/artifacts/ila6g1h4_poloroid-frame.png')`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  transform: isDesktop ? "rotate(3deg)" : "rotate(-7deg)",
                  zIndex: 20,
                }}
              ></div>
            )}

            {/* Pasta salad sticky note - right */}
            <div
              className="absolute z-20"
              style={{
                left: isDesktop ? "calc(50% + 295px)" : "calc(50% + 150px)",
                top: isDesktop ? "calc(50% - 50px)" : "calc(50% + 40px)",
                width: isDesktop ? "270px" : "200px",
                height: isDesktop ? "270px" : "200px",
                backgroundImage: `url('/pasta-salad-note.png')`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                transform: "rotate(8deg)",
                display: isMobile ? "none" : "block",
              }}
            ></div>
          </div>

          {/* Center blue scalloped paper */}
          <div
            className="relative z-10 text-center w-full mx-4 lg:mx-0"
            style={{
              backgroundImage: isMobile
                ? `url('https://customer-assets.emergentagent.com/job_recipemanager-1/artifacts/ooxi7c8c_mobile-blue-paper.png')`
                : isDesktop
                ? `url('https://customer-assets.emergentagent.com/job_recipehub-15/artifacts/7n75ztus_blue-paper-wide-desktop.png')`
                : `url('https://customer-assets.emergentagent.com/job_cookbookai/artifacts/2z47xjwk_tablet%20blue%20paper.png')`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              minHeight: isMobile ? "690px" : isDesktop ? "604px" : "540px",
              maxWidth: isDesktop ? "57.13rem" : isMobile ? "402px" : "594px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: isDesktop ? "84.92px" : isMobile ? "60px" : "100px",
              paddingBottom: isDesktop ? "109.08px" : isMobile ? "60px" : "100px",
              paddingLeft: isDesktop ? "65px" : isMobile ? "50px" : "80px",
              paddingRight: isDesktop ? "65px" : isMobile ? "50px" : "80px",
            }}
          >
            {children({ isDesktop, isMobile })}
          </div>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

const InviteHeading = ({ isDesktop, isMobile, children }) => (
  <h2
    className={`font-bold ${isMobile ? "" : "mb-4 md:mb-6"} ${isDesktop ? "text-5xl" : "text-3xl"}`}
    style={{
      color: BROWN,
      fontFamily: "'Instrument Serif', serif",
      lineHeight: "1.2",
      maxWidth: isDesktop ? "70%" : isMobile ? "100%" : "80%",
      margin: "0 auto",
      marginBottom: isMobile ? "16px" : undefined,
    }}
  >
    {children}
  </h2>
);

const InviteBody = ({ isDesktop, isMobile, children }) => (
  <p
    className={`${isMobile ? "" : "mb-6 md:mb-8"} ${isDesktop ? "text-xl" : "text-base"}`}
    style={{
      color: BROWN,
      fontFamily: "'Bricolage Grotesque', sans-serif",
      maxWidth: isDesktop ? "60%" : isMobile ? "100%" : "72%",
      margin: isMobile ? "0 auto" : "0 auto 2rem auto",
      marginBottom: isMobile ? "16px" : undefined,
    }}
  >
    {children}
  </p>
);

const inviteButtonClasses = (isMobile) =>
  `font-medium transition-colors ${isMobile ? "w-full px-8 py-4 text-lg" : "px-6 py-3 md:px-8 md:py-4 text-base md:text-lg"} text-white`;

const InvitePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  // Tie results to this code so changing invitations cannot reuse a prior lookup.
  const [lookupResult, setLookupResult] = useState({ code, status: "loading" });
  const status = lookupResult.code === code ? lookupResult.status : "loading";
  const [resending, setResending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const lookup = async () => {
      try {
        const resp = await axios.post(`${API}/invites/lookup`, { code });
        if (cancelled) return;
        const { status: s, email } = resp.data || {};
        if ((s === "valid" || s === "signup_required") && email) {
          // Fill the existing form in place: a route handoff remounts the dialog
          // and loses focus, drafts, and the user's dismiss/login intent.
          setLookupResult({ code, status: "verified", verifiedInvite: { code, email } });
        } else {
          setLookupResult({ code, status: s === "expired" || s === "used" ? s : "invalid" });
        }
      } catch (e) {
        if (!cancelled) setLookupResult({ code, status: e.response?.status === 503 ? "paused" : "invalid" });
      }
    };
    lookup();
    return () => { cancelled = true; };
  }, [code]);

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/invites/resend`, { code });
      setLookupResult({ code, status: "resent" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send a new invite. Please try again.");
    } finally {
      setResending(false);
    }
  };

  if (status === "paused") {
    return (
      <InviteHeroShell testId="invite-inbox-verification">
        {({ isDesktop, isMobile }) => (
          <>
            <InviteHeading isDesktop={isDesktop} isMobile={isMobile}>
              Invitations are paused
            </InviteHeading>
            <InviteBody isDesktop={isDesktop} isMobile={isMobile}>
              Please try again later.
            </InviteBody>
          </>
        )}
      </InviteHeroShell>
    );
  }

  if (status === "loading" || status === "verified") {
    return (
      <LandingPage
        key={code}
        inviteLookupPending={status === "loading"}
        verifiedInvite={status === "verified" ? lookupResult.verifiedInvite : null}
      />
    );
  }

  if (status === "expired") {
    return (
      <InviteHeroShell testId="invite-page-expired">
        {({ isDesktop, isMobile }) => (
          <>
            <InviteHeading isDesktop={isDesktop} isMobile={isMobile}>
              This invite has expired
            </InviteHeading>
            <div className={`flex gap-3 items-center justify-center ${isMobile ? "flex-col w-full" : "flex-row"}`} style={{ position: "relative", zIndex: 30 }}>
              <Button
                size="lg"
                onClick={handleResend}
                disabled={resending}
                data-testid="invite-resend-button"
                className={inviteButtonClasses(isMobile)}
                style={{
                  backgroundColor: BROWN,
                  borderColor: BROWN,
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                }}
              >
                {resending ? "Sending…" : "Send another"}
              </Button>
            </div>
          </>
        )}
      </InviteHeroShell>
    );
  }

  if (status === "resent") {
    return (
      <InviteHeroShell testId="invite-page-resent">
        {({ isDesktop, isMobile }) => (
          <>
            <InviteHeading isDesktop={isDesktop} isMobile={isMobile}>
              A new invite is on its way!
            </InviteHeading>
            <InviteBody isDesktop={isDesktop} isMobile={isMobile}>
              Check your inbox — we just emailed you a fresh invitation link.
              It will be valid for the next 48 hours.
            </InviteBody>
          </>
        )}
      </InviteHeroShell>
    );
  }

  if (status === "used") {
    return (
      <InviteHeroShell testId="invite-page-used">
        {({ isDesktop, isMobile }) => (
          <>
            <InviteHeading isDesktop={isDesktop} isMobile={isMobile}>
              This invite was already used
            </InviteHeading>
            <InviteBody isDesktop={isDesktop} isMobile={isMobile}>
              An account has already been created with this invitation. If that
              was you, just log in below.
            </InviteBody>
            <div className={`flex gap-3 items-center justify-center ${isMobile ? "flex-col w-full" : "flex-row"}`} style={{ position: "relative", zIndex: 30 }}>
              <Button
                size="lg"
                onClick={() => navigate("/?login=true")}
                className={inviteButtonClasses(isMobile)}
                style={{
                  backgroundColor: BROWN,
                  borderColor: BROWN,
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                }}
              >
                Log in
              </Button>
            </div>
          </>
        )}
      </InviteHeroShell>
    );
  }

  return (
    <InviteHeroShell testId="invite-page-invalid">
      {({ isDesktop, isMobile }) => (
        <>
          <InviteHeading isDesktop={isDesktop} isMobile={isMobile}>
            Invitation not found
          </InviteHeading>
          <InviteBody isDesktop={isDesktop} isMobile={isMobile}>
            This invitation link isn't valid. It may have been replaced by a
            newer one — check your inbox for the most recent email from Wellby.
          </InviteBody>
        </>
      )}
    </InviteHeroShell>
  );
};

export default InvitePage;
